// Document generation utility for drawdown analysis reports
// Based on the old implementation using docx library

import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  HeadingLevel,
  AlignmentType,
  VerticalAlign,
  WidthType,
  FootnoteReferenceRun,
  Packer,
} from "docx";
import { saveAs } from "file-saver";

/**
 * Generates a styled Word document for drawdown analysis report
 * @param {Object} runDetails - Run details from the API
 * @param {Array} reportData - Report data array from the API
 */
export const generateDrawdownReport = (runDetails, reportData) => {
  const doc = createDocument(runDetails, reportData);

  Packer.toBlob(doc).then((blob) => {
    const filename = `drawdown-analysis-${runDetails.proposed_well_name}-run-${runDetails.run_ndx}.docx`;
    saveAs(blob, filename);
  });
};

const createDocument = (runDetails, reportData) => {
  return new Document({
    styles: {
      default: {
        heading2: {
          run: {
            size: 22,
            bold: false,
            italics: false,
            font: "Calibri (body)",
          },
          paragraph: {
            spacing: {
              after: 160,
              line: 260,
            },
          },
        },
        heading3: {
          run: {
            size: 22,
            bold: false,
            italics: false,
            font: "Calibri (body)",
          },
          paragraph: {
            spacing: {
              before: 2040,
              line: 260,
            },
          },
        },
      },
    },
    footnotes: {
      1: {
        children: [
          new Paragraph({
            text: " Theis, C.V., 1935, The Relation Between the Lowering of the Piezometric Surface and the Rate and Duration of Discharge of a Well Using Ground-Water Storage: American Geophysical Union Transactions, v. 16, p. 519-524.",
            font: {
              name: "Calibri (body)",
            },
            indent: {
              firstLine: 0,
              left: 400,
              hanging: 400,
            },
          }),
        ],
      },
      2: {
        children: [
          new Paragraph({
            text: " Groundwater availability model (GAM) created by LRE Water and KT Groundwater specifically to cover Williamson County and its underlying aquifer systems.",
            font: {
              name: "Calibri (body)",
            },
            indent: {
              firstLine: 0,
              left: 400,
              hanging: 400,
            },
          }),
        ],
      },
    },
    sections: [
      {
        properties: {},
        children: [
          // Header with well information
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "Proposed Well ID: ",
                bold: true,
              }),
              new TextRun({
                text: `${runDetails.proposed_well_name}`,
                italics: true,
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "Aquifer: ",
                bold: true,
              }),
              new TextRun({
                text: `${runDetails.source_aquifer}`,
                italics: true,
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "Proposed Annual Production: ",
                bold: true,
              }),
              new TextRun({
                text: `${runDetails.annual_af_requested}`,
                italics: true,
              }),
              new TextRun({
                text: " Acre-Feet per Year",
                italics: false,
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "Proposed Instantaneous Pumping Rate: ",
                bold: true,
              }),
              new TextRun({
                text: `${runDetails.discharge_gpm}`,
                italics: true,
              }),
              new TextRun({
                text: " Gallons per Minute",
                italics: true,
              }),
            ],
          }),

          // Analysis explanation
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "The potential effects of the proposed production on local water levels in the aquifer are calculated using the Theis equation",
              }),
              new FootnoteReferenceRun(1),
              new TextRun({
                text: " which relates water level decline (that is, drawdown) to the pumping rate of a well and properties of the aquifer. While the equation does not account for aquifer conditions which may affect the calculation of long-term water level declines (for example: aquifer recharge, faulting, or changes in aquifer structure), it does provide a very good, reliable, and straightforward method for estimating relatively short-term drawdown in and near a well due to pumping. To assess the potential effects from the proposed production, the equation uses values from the groundwater availability model datasets",
              }),
              new FootnoteReferenceRun(2),
              new TextRun({
                text: ".",
              }),
            ],
          }),

          // Table introduction
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "The following table presents the calculated drawdown at the proposed well and at other nearby wells completed in the same aquifer. For 1-Day Drawdown, we applied the proposed instantaneous pumping rate for a period of 24 hours. For 30-Day Drawdown, we assumed peak pumping during the summer of about 15 percent more than the average monthly amount (that is, the proposed annual production rate divided by 12 then multiplied by 1.15). For 1-Year Drawdown, we used the proposed annual production amount.",
              }),
            ],
          }),

          // Results table
          createResultsTable(reportData),

          // Disclaimer
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "",
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "The predicted drawdown presented above is based on our current understanding of the aquifer hydraulic properties and the estimated production from the proposed well. The predicted drawdown values presented do not include the effects from other wells pumping near the proposed well. Predicted drawdown of less than one (1) foot is considered negligible for analysis purposes due to inherent uncertainty in the aquifer hydraulic characteristics and a difference in the estimated retail electricity costs for a typical domestic well being less than $0.10.",
              }),
            ],
          }),

          // Recommendations section
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [
              new TextRun({
                text: "Recommendations",
                font: {
                  name: "Calibri Light",
                },
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "To assess actual changes in water levels due to pumping from the proposed well, the well driller should ensure there is a removable plug in the sanitary seal to allow clear access into the well for water level measurement by the District. In addition, if space allows, the pump installer should install a measuring tube alongside the column pipe to allow for measurement of the water level using an e-line or other direct measurement method. Upon discussion and coordination with the District, other automated water level monitoring methods may be considered for installation with the well.",
              }),
            ],
          }),

          // Geoscientist seal section
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [
              new TextRun({
                text: "Geoscientist Seal",
                font: {
                  name: "Calibri Light",
                },
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "The following licensed professional geoscientist(s) have reviewed the results and recommendations presented in this report of the potential effects due to production from a proposed well.",
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_3,
            children: [
              new TextRun({
                text: "___________________________________",
              }),
            ],
          }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: "Name",
              }),
              new TextRun({
                text: "\tSeal",
              }),
            ],
            tabStops: [
              {
                type: "left",
                position: 6480,
              },
            ],
          }),
        ],
      },
    ],
  });
};

const createResultsTable = (reportData) => {
  return new Table({
    columnWidths: [1802, 1802, 1802, 1802, 1802],
    rows: [
      // Header row
      new TableRow({
        children: [
          new TableCell({
            verticalAlign: VerticalAlign.BOTTOM,
            width: {
              size: 1802,
              type: WidthType.DXA,
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: "Well Name",
                    size: 20,
                    bold: true,
                    font: { name: "Calibri (body)" },
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.BOTTOM,
            width: {
              size: 1802,
              type: WidthType.DXA,
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: "Distance from Proposed Well (feet)",
                    size: 20,
                    bold: true,
                    font: { name: "Calibri (body)" },
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.BOTTOM,
            width: {
              size: 1802,
              type: WidthType.DXA,
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: "1-Day Drawdown (feet)",
                    size: 20,
                    bold: true,
                    font: { name: "Calibri (body)" },
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.BOTTOM,
            width: {
              size: 1802,
              type: WidthType.DXA,
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: "30-Day Drawdown (feet)",
                    size: 20,
                    bold: true,
                    font: { name: "Calibri (body)" },
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.BOTTOM,
            width: {
              size: 1802,
              type: WidthType.DXA,
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: "1-Year Drawdown (feet)",
                    size: 20,
                    bold: true,
                    font: { name: "Calibri (body)" },
                  }),
                ],
              }),
            ],
          }),
        ],
      }),

      // Data rows
      ...reportData.map((item) => {
        return new TableRow({
          children: [
            new TableCell({
              verticalAlign: VerticalAlign.BOTTOM,
              width: {
                size: 1802,
                type: WidthType.DXA,
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: `${item.well_name || ""}`,
                      size: 20,
                      bold: true,
                      font: { name: "Calibri (body)" },
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.BOTTOM,
              width: {
                size: 1802,
                type: WidthType.DXA,
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text:
                        item.distance_from_well_ft === 0
                          ? "---"
                          : `${Math.round(item.distance_from_well_ft || 0)}`,
                      size: 20,
                      font: { name: "Calibri (body)" },
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.BOTTOM,
              width: {
                size: 1802,
                type: WidthType.DXA,
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: `${item.drawdown_1day || 0}`,
                      size: 20,
                      font: { name: "Calibri (body)" },
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.BOTTOM,
              width: {
                size: 1802,
                type: WidthType.DXA,
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: `${item.drawdown_30day || 0}`,
                      size: 20,
                      font: { name: "Calibri (body)" },
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.BOTTOM,
              width: {
                size: 1802,
                type: WidthType.DXA,
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({
                      text: `${item.drawdown_1year || 0}`,
                      size: 20,
                      font: { name: "Calibri (body)" },
                    }),
                  ],
                }),
              ],
            }),
          ],
        });
      }),
    ],
  });
};
